import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import FeaturedSection from "@/components/property/FeaturedSection";
import PopularCitiesSection from "@/components/home/PopularCitiesSection";
import { clearMiniSiteContext } from "@/lib/minisiteContext";
import { usePublicSiteCms, orderedSections } from "@/lib/siteCms";
import {
  MapPin, Search, Wallet, Sparkles, Star, ShieldCheck, Building2, ArrowRight,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1750420556288-d0e32a6f517b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjb3p5JTIwbW9kZXJuJTIwYmVkcm9vbSUyMGludGVyaW9yJTIwdHJvcGljYWx8ZW58MHx8fHwxNzg3NTU5MDQ0fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" } }),
};

const values = [
  { key: "lokasi", icon: MapPin, title: "Lokasi & Jarak", desc: "Cari hunian dekat kampus atau kantor tujuanmu." },
  { key: "harga", icon: Wallet, title: "Harga Transparan", desc: "Bandingkan harga sewa tanpa biaya tersembunyi." },
  { key: "fasilitas", icon: Sparkles, title: "Fasilitas Lengkap", desc: "Filter berdasarkan WiFi, AC, dapur, dan lainnya." },
  { key: "rating", icon: Star, title: "Rating & Kualitas", desc: "Ulasan asli dari penghuni sebelumnya." },
];

const WHY_ICONS = { lokasi: MapPin, harga: Wallet, fasilitas: Sparkles, rating: Star };

const types = [
  { name: "Kos", value: "kos" },
  { name: "Apartemen", value: "apartemen" },
  { name: "Kontrakan", value: "kontrakan" },
  { name: "Co-living", value: "coliving" },
];

// Fallback section order used until the CMS config loads.
const DEFAULT_ORDER = [
  { key: "featured", enabled: true, order: 1 },
  { key: "why_avasa", enabled: true, order: 2 },
  { key: "popular_cities", enabled: true, order: 3 },
  { key: "housing_types", enabled: true, order: 4 },
  { key: "cta", enabled: true, order: 5 },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [heroQ, setHeroQ] = useState("");
  const cms = usePublicSiteCms();
  // Opening the AVASA homepage directly ends any web-site visit context.
  useEffect(() => { clearMiniSiteContext(); }, []);
  const goSearch = () => {
    const q = heroQ.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const hero = cms?.hero || {};
  const heroBg = hero.bg_image || HERO_IMG;
  const heroTitle = hero.title || "Temukan *hunian* yang terasa seperti rumah.";
  const heroAlign = hero.alignment === "left" ? "text-left" : hero.alignment === "right" ? "text-right" : "text-center";
  const sectionList = cms ? orderedSections(cms) : DEFAULT_ORDER;

  // "*kata*" pada judul hero ditampilkan dengan warna aksen (seperti desain asli).
  const renderHeroTitle = (text) =>
    text.split(/(\*[^*]+\*)/g).map((part, i) =>
      part.startsWith("*") && part.endsWith("*") && part.length > 2
        ? <span key={i} className="text-accent">{part.slice(1, -1)}</span>
        : <span key={i}>{part}</span>
    );

  const renderSection = (s) => {
    switch (s.key) {
      case "featured":
        return <FeaturedSection key="featured" />;
      case "popular_cities":
        return <PopularCitiesSection key="popular_cities" />;
      case "why_avasa":
        return (
          <section key="why_avasa" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{s.title || "Kenapa AVASA"}</span>
              <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-semibold tracking-tight">
                {s.subtitle || "Cari hunian dengan cara yang lebih cerdas"}
              </h2>
            </div>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {(cms?.why_avasa_cards?.length ? cms.why_avasa_cards : values).map((v, i) => {
                const Icon = WHY_ICONS[v.key] || Sparkles;
                return (
                  <motion.div key={v.key || v.title} custom={i} initial="hidden" whileInView="show"
                    viewport={{ once: true, margin: "-60px" }} variants={fadeUp}>
                    <Link to={`/kenapa-avasa/${v.key}`}
                      className="group block h-full bg-card border border-border rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 transition-all"
                      data-testid={`value-card-${i}`}>
                      <span className="grid place-items-center w-11 h-11 rounded-xl bg-secondary text-primary mb-4"><Icon size={20} /></span>
                      <h3 className="font-heading text-lg font-medium">{v.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`value-card-more-${i}`}>
                        Selengkapnya <ArrowRight size={13} />
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </section>
        );
      case "housing_types":
        return (
          <section key="housing_types" className="bg-secondary/40 border-y border-border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-w-xl">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{s.title || "Tipe Hunian"}</span>
                  <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-semibold tracking-tight">
                    {s.subtitle || "Mulai dari kos, berkembang ke semua jenis hunian"}
                  </h2>
                </div>
              </div>
              <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
                {types.map((t) => (
                  <button key={t.name} type="button" onClick={() => navigate(`/search?type=${t.value}`)}
                    data-testid={`housing-type-${t.name.toLowerCase()}`}
                    className="text-left rounded-2xl p-6 border border-border bg-card transition-transform hover:-translate-y-1 hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/40">
                    <Building2 size={22} className="text-primary" />
                    <h3 className="mt-4 font-heading text-xl font-semibold">{t.name}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">Lihat hunian <ArrowRight size={13} /></span>
                  </button>
                ))}
              </div>
            </div>
          </section>
        );
      case "cta":
        return (
          <section key="cta" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 lg:p-16 relative overflow-hidden">
              <div className="relative z-10 max-w-2xl">
                <ShieldCheck size={32} className="mb-4" />
                <h2 className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight">
                  {s.title || "Siap menemukan hunian impianmu?"}
                </h2>
                <p className="mt-4 text-primary-foreground/80 leading-relaxed">
                  {s.subtitle || "Bergabung dengan AVASA hari ini — gratis untuk mulai mencari."}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/register" data-testid="cta-register-link"
                    className="px-7 py-3.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 active:scale-95 transition-transform">Daftar Sekarang</Link>
                  <Link to="/search" data-testid="cta-search-link"
                    className="px-7 py-3.5 rounded-full border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors">Jelajahi Hunian</Link>
                </div>
              </div>
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div data-testid="landing-page">
      {/* HERO — CMS-driven */}
      {hero.enabled !== false && (
      <section className="relative overflow-hidden min-h-[70vh] flex flex-col">
        <div className="absolute inset-0">
          <img src={heroBg} alt="Interior hunian modern" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/15 to-black/35" />
        </div>
        <motion.div
          className={`relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 lg:pt-20 pb-8 sm:pb-10 flex-1 flex flex-col justify-between ${heroAlign}`}
          initial="hidden" animate="show" variants={fadeUp}
        >
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md text-xs font-bold uppercase tracking-[0.2em] text-white">
              <Building2 size={14} /> {cms?.branding?.brand_name ? `${cms.branding.brand_name} · Marketplace Hunian` : "Marketplace Hunian Indonesia"}
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] drop-shadow-[0_2px_16px_rgba(0,0,0,0.45)]">
              {renderHeroTitle(heroTitle)}
            </h1>
            {hero.subtitle !== "" && (
              <p className="mt-6 text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto drop-shadow-[0_1px_10px_rgba(0,0,0,0.5)]">
                {hero.subtitle || "AVASA membantumu menemukan kos dan hunian sewa berdasarkan lokasi, jarak, harga, fasilitas, dan rating — semua di satu tempat."}
              </p>
            )}
          </div>
          <div className="mt-10">
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => navigate(hero.cta_url || "/search")} data-testid="hero-search-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 active:scale-95 transition-transform">
                {hero.cta_text || "Mulai Mencari"} <ArrowRight size={18} />
              </button>
              <button onClick={() => navigate("/register")} data-testid="hero-register-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/40 bg-card text-primary font-semibold hover:-translate-y-0.5 transition-transform">
                Daftarkan Properti
              </button>
            </div>
            <div className="mt-4 max-w-2xl mx-auto bg-card border border-border rounded-2xl p-4 shadow-lg" data-testid="hero-search-card">
              <div className="flex items-center gap-2 bg-secondary/50 rounded-xl px-4 py-3">
                <Search size={18} className="text-muted-foreground" />
                <input data-testid="hero-search-input" value={heroQ} onChange={(e) => setHeroQ(e.target.value)}
                  placeholder="Cari kos di Jakarta, Bandung, Yogyakarta..."
                  className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
                  onKeyDown={(e) => e.key === "Enter" && goSearch()} />
                <button onClick={goSearch} data-testid="hero-card-search-button"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:scale-105 transition-transform">Cari</button>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
      )}

      {sectionList.map(renderSection)}
    </div>
  );
}
